import React, { useState, useEffect } from "react";
import { api, formatApiError, formatDuration } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Barbell, PencilSimple, Trash, Note, VideoCamera, Shield, ArrowSquareOut, Fire, CaretUp, CaretDown, BookmarkSimple, CheckCircle, LinkSimple, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";
import { extractYouTubeId } from "./blockMeta";
import MovementForm from "./MovementForm";
import LogSetButton from "./LogSetButton";
import RestTimer from "./RestTimer";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import { formatStoredWeight } from "@/components/app/WeightInput";
import { formatStoredDistance, formatStoredHeight } from "@/lib/units";

export default function MovementCard({ mv, program, dayIdx, blockId, blockType, readOnly, onEdited, onDeleted, canEditCore, athleteId, hideLogButton, onMoveUp, onMoveDown, canMoveUp, canMoveDown, isSupersetMember, isSupersetHead, supersetNumber, onToggleSuperset }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const mvUnit = mv.weight_unit || userUnit;
  const [editOpen, setEditOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  // Rest-timer state: incremented on each successful log → forces RestTimer
  // to reset its clock. Null = hidden until next log.
  const [restRunId, setRestRunId] = useState(null);
  const isStrengthLike = blockType === "strength" || blockType === "circuit";

  // Persistent "X of Y sets" counter. Pulled from the backend on mount so it
  // survives navigation — leave the screen, come back, the count is intact.
  // Scoped to this exact movement on this exact program day, so the same
  // movement programmed on Tuesday vs Friday is tracked separately.
  const [loggedSets, setLoggedSets] = useState(0);
  useEffect(() => {
    if (!isStrengthLike || readOnly || !athleteId || !program?.id || !mv?.id) return;
    let alive = true;
    api.get("/workout-logs", {
      params: { athlete_id: athleteId, program_id: program.id, day_of_week: dayIdx, movement_id: mv.id, limit: 200 },
    }).then((r) => {
      if (alive) setLoggedSets(Array.isArray(r.data) ? r.data.length : 0);
    }).catch(() => { /* non-fatal — leave at 0 */ });
    return () => { alive = false; };
  }, [isStrengthLike, readOnly, athleteId, program?.id, dayIdx, mv?.id]);

  const [savedToLib, setSavedToLib] = useState(false);
  const [savingLib, setSavingLib] = useState(false);
  const [libNames, setLibNames] = useState(null);   // null = not loaded yet

  // Lazy-load coach library names so we know if THIS movement is already there.
  React.useEffect(() => {
    if (!canEditCore || libNames !== null) return;
    let alive = true;
    api.get("/exercises").then((r) => {
      if (!alive) return;
      setLibNames(new Set(r.data.map((e) => e.name.trim().toLowerCase())));
    }).catch(() => alive && setLibNames(new Set()));
    return () => { alive = false; };
  }, [canEditCore, libNames]);

  const isInLibrary =
    savedToLib ||
    (libNames !== null && libNames.has((mv.name || "").trim().toLowerCase()));

  const saveToLibrary = async () => {
    setSavingLib(true);
    try {
      await api.post("/exercises", {
        name: mv.name,
        sets: mv.sets ?? null,
        reps: mv.reps ?? null,
        weight: mv.weight ?? null,
        weight_unit: mv.weight_unit ?? null,
        time_seconds: mv.time_seconds ?? null,
        distance_meters: mv.distance_meters ?? null,
        distance_unit: mv.distance_unit ?? null,
        height_inches: mv.height_inches ?? null,
        height_unit: mv.height_unit ?? null,
        calories: mv.calories ?? null,
        rpe: mv.rpe ?? null,
        notes: mv.notes || null,
        video_url: mv.video_url || null,
        form_notes: mv.form_notes || null,
      });
      setSavedToLib(true);
      toast.success(`Added ${mv.name} to the shared library`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLib(false);
    }
  };

  const scopeUrl = blockId
    ? `/programs/${program.id}/days/${dayIdx}/blocks/${blockId}/movements/${mv.id}`
    : `/programs/${program.id}/days/${dayIdx}/movements/${mv.id}`;

  const del = async () => {
    if (!window.confirm(`Remove ${mv.name}?`)) return;
    try {
      await api.delete(scopeUrl);
      toast.success("Movement removed");
      onDeleted(mv.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async (patch) => {
    try {
      const { data } = await api.patch(scopeUrl, patch);
      toast.success("Updated");
      setEditOpen(false);
      onEdited(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className={`hs-card p-5 animate-fade-up relative ${mv.superset_group_id ? "border-l-4 border-l-terracotta" : ""}`}
      data-testid={`movement-${mv.id}`}
    >
      {mv.superset_group_id && (
        <span
          className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-terracotta text-white text-[10px] font-bold uppercase tracking-[0.14em] shadow-sm"
          data-testid={`superset-chip-${mv.id}`}
        >
          {supersetNumber ? `Superset ${supersetNumber}` : (isSupersetHead ? "Superset ↓" : "Superset ↑")}
        </span>
      )}
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Barbell size={18} className="text-moss shrink-0" />
            <h4 className="font-heading text-lg font-semibold break-words">{mv.name}</h4>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 font-data text-sm">
            {isStrengthLike && mv.sets ? (
              <span
                className={`px-2.5 py-1 rounded-lg font-semibold inline-flex items-center gap-1.5 ${
                  loggedSets >= (mv.sets || 0)
                    ? "bg-moss text-white"
                    : loggedSets > 0
                      ? "bg-terracotta/15 text-terracotta"
                      : "bg-muted2 text-ink"
                }`}
                title={`${loggedSets} of ${mv.sets} sets logged${loggedSets >= (mv.sets || 0) ? " — complete!" : ""}`}
                data-testid={`sets-progress-${mv.id}`}
              >
                {loggedSets >= (mv.sets || 0) && <CheckCircle size={12} weight="fill" />}
                {loggedSets} of {mv.sets} sets
              </span>
            ) : mv.sets ? (
              <span className="px-2.5 py-1 rounded-lg bg-muted2">{mv.sets} sets</span>
            ) : null}
            {mv.reps_scheme?.length
              ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold" title="Reps per set">{mv.reps_scheme.join("-")} reps</span>
              : mv.reps ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{mv.reps} reps</span> : null}
            {mv.weight_scheme?.length
              ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold" title="Weight per set">@ {mv.weight_scheme.map((w) => formatStoredWeight(w, mvUnit).replace(/\s.*$/, "")).join("-")} {mvUnit}</span>
              : mv.weight ? <span className="px-2.5 py-1 rounded-lg bg-sage text-moss font-semibold">@ {formatStoredWeight(mv.weight, mvUnit)}</span> : null}
            {mv.time_seconds ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{formatDuration(mv.time_seconds)}</span> : null}
            {mv.distance_scheme?.length
              ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold" title="Distance per set">{mv.distance_scheme.map((d) => formatStoredDistance(d, mv.distance_unit || "m").replace(/\s.*$/, "")).join("-")} {mv.distance_unit || "m"}</span>
              : mv.distance_meters ? <span className="px-2.5 py-1 rounded-lg bg-muted2">{formatStoredDistance(mv.distance_meters, mv.distance_unit || "m")}</span> : null}
            {mv.height_scheme?.length
              ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold" title="Height per set">↕ {mv.height_scheme.map((h) => formatStoredHeight(h, mv.height_unit || "in").replace(/\s.*$/, "")).join("-")} {mv.height_unit || "in"}</span>
              : mv.height_inches ? <span className="px-2.5 py-1 rounded-lg bg-muted2">↕ {formatStoredHeight(mv.height_inches, mv.height_unit || "in")}</span> : null}
            {mv.calories_scheme?.length
              ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold inline-flex items-center gap-1" title="Calories per set"><Fire size={12} weight="fill" /> {mv.calories_scheme.join("-")} cal</span>
              : mv.calories ? <span className="px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta font-semibold inline-flex items-center gap-1"><Fire size={12} weight="fill" /> {mv.calories} cal</span> : null}
            {mv.rpe ? <span className="px-2.5 py-1 rounded-lg bg-muted2">RPE {mv.rpe}</span> : null}
          </div>
        </div>
        {!readOnly && !hideLogButton && (
          <button
            type="button"
            onClick={() => setLogOpen(true)}
            className="w-full h-10 rounded-full bg-terracotta hover:bg-terracotta-hover text-white shadow-sm font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            data-testid={`float-log-set-${mv.id}`}
            aria-label={`Log set for ${mv.name}`}
          >
            <CheckCircle size={16} weight="bold" /> Log set
          </button>
        )}
        <div className="flex items-center gap-1 shrink-0 flex-wrap">
          {canEditCore && (onMoveUp || onMoveDown) && (
            <div className="flex flex-row -my-0.5 mr-0.5">
              <Button
                size="icon" variant="ghost"
                className="rounded-md h-7 w-7 hover:bg-muted2 disabled:opacity-30"
                onClick={() => onMoveUp?.(mv.id)}
                disabled={!canMoveUp}
                title="Move up"
                aria-label={`Move ${mv.name} up`}
                data-testid={`move-movement-up-${mv.id}`}
              >
                <CaretUp size={14} weight="bold" />
              </Button>
              <Button
                size="icon" variant="ghost"
                className="rounded-md h-7 w-7 hover:bg-muted2 disabled:opacity-30"
                onClick={() => onMoveDown?.(mv.id)}
                disabled={!canMoveDown}
                title="Move down"
                aria-label={`Move ${mv.name} down`}
                data-testid={`move-movement-down-${mv.id}`}
              >
                <CaretDown size={14} weight="bold" />
              </Button>
            </div>
          )}
          {canEditCore && !isInLibrary && (
            <Button
              size="sm"
              onClick={saveToLibrary}
              disabled={savingLib}
              className="rounded-xl h-9 px-3 text-xs font-semibold uppercase tracking-wider bg-terracotta hover:bg-terracotta-hover text-white hover:text-white shadow-sm"
              title="Save this movement to your library so it autofills next time"
              data-testid={`save-movement-to-library-${mv.id}`}
            >
              <BookmarkSimple size={14} weight="fill" className="mr-1.5" />
              {savingLib ? "Saving…" : "Save to library"}
            </Button>
          )}
          {canEditCore && onToggleSuperset && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onToggleSuperset(mv.id)}
              className={`rounded-lg h-9 w-9 ${
                mv.superset_group_id ? "text-terracotta hover:bg-terracotta/10" : "hover:bg-muted2"
              }`}
              title={
                mv.superset_group_id
                  ? "Remove from superset"
                  : "Link to the movement above as a superset"
              }
              data-testid={`toggle-superset-${mv.id}`}
            >
              {mv.superset_group_id
                ? <LinkBreak size={15} weight="bold" />
                : <LinkSimple size={15} weight="bold" />}
            </Button>
          )}
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button size="icon" variant="ghost" className="rounded-lg hover:bg-muted2" data-testid={`edit-movement-${mv.id}`}>
                <PencilSimple size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-h-[92vh] overflow-hidden flex flex-col">
              <DialogHeader><DialogTitle className="font-heading">Edit movement</DialogTitle></DialogHeader>
              <MovementForm initial={mv} onSave={save} onCancel={() => setEditOpen(false)} readOnlyCore={!canEditCore} />
            </DialogContent>
          </Dialog>
          {canEditCore && (
            <Button size="icon" variant="ghost" onClick={del} className="rounded-lg text-error hover:bg-red-50 hover:text-error" data-testid={`delete-movement-${mv.id}`}>
              <Trash size={16} />
            </Button>
          )}
        </div>
      </div>

      {(mv.notes || mv.form_notes || mv.video_url) && (
        <div className="mt-3 space-y-3 [overflow-wrap:anywhere]">
          {mv.notes && (
            <div className="flex items-start gap-2 text-sm text-ink-2">
              <Note size={14} className="mt-1 shrink-0" />
              <p className="flex-1 min-w-0">{mv.notes}</p>
            </div>
          )}
          {mv.form_notes && (
            <div className="p-3 rounded-xl bg-sage/60 border border-sage" data-testid={`form-notes-${mv.id}`}>
              <div className="flex items-center gap-1.5 hs-label text-[10px] text-moss">
                <Shield size={12} weight="bold" /> Form check
              </div>
              <p className="mt-1 text-sm text-moss-hover leading-relaxed">{mv.form_notes}</p>
            </div>
          )}
          {mv.video_url && (() => {
            const ytId = extractYouTubeId(mv.video_url);
            return (
              <a
                href={mv.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center gap-3 p-2 pr-4 rounded-xl border border-line bg-muted2 hover:bg-sage transition-colors"
                data-testid={`video-link-${mv.id}`}
              >
                <div className="relative h-14 w-24 rounded-lg overflow-hidden bg-ink shrink-0">
                  {ytId ? (
                    <img
                      src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                      alt={`${mv.name} demo`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full grid place-items-center">
                      <VideoCamera size={20} className="text-white/70" />
                    </div>
                  )}
                  <div className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/10 transition-colors">
                    <div className="h-8 w-8 rounded-full bg-white/90 grid place-items-center">
                      <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[10px] border-t-transparent border-b-transparent border-l-ink ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="hs-label text-[10px] text-terracotta flex items-center gap-1">
                    <VideoCamera size={12} /> Demo video
                  </div>
                  <div className="text-sm text-ink font-medium truncate">Watch how to move</div>
                </div>
                <ArrowSquareOut size={14} className="text-ink-3 group-hover:text-ink shrink-0" />
              </a>
            );
          })()}
        </div>
      )}

      {!readOnly && !hideLogButton && (
        <>
          {/* Log-set dialog stays mounted (controlled via logOpen); the visible
              trigger is the pill rendered inline above the action icons — this
              keeps the movement title/description fully visible on narrow
              day columns where an absolutely-positioned pill would overlap. */}
          <LogSetButton
            mv={mv}
            program={program}
            dayIdx={dayIdx}
            blockId={blockId}
            blockType={blockType}
            athleteId={athleteId}
            open={logOpen}
            setOpen={setLogOpen}
            trigger={<span className="hidden" aria-hidden="true" />}
            onLogged={() => {
              setLoggedSets((n) => n + 1);
              if (isStrengthLike) setRestRunId((n) => (n ?? 0) + 1);
            }}
          />
          {isStrengthLike && restRunId !== null && (
            <RestTimer
              runId={restRunId}
              onHide={() => setRestRunId(null)}
              testIdSuffix={mv.id}
            />
          )}
        </>
      )}
    </div>
  );
}
